/**
 * OnLeaveBanner — iter140
 * ─────────────────────────────────────────────────────────────────────────
 * Shows every approved leave at the current site whose end_date ≥ today,
 * sorted by start_date ASC. Active-now leaves get an amber chip; future
 * leaves get a slate chip. Empty state renders nothing.
 *
 * Mounted in two places:
 *   • /job-cards header row (next to the calendar / Create Job Card btn)
 *   • Super Admin Overview tab (as the 3rd widget below P&L + Workshop Ops)
 *
 * Refreshes every 5 min — leaves change infrequently, so this is plenty.
 * Renders as inline pills (compact) by default; pass `variant="card"` for
 * the Overview tab card layout.
 */
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Plane, CalendarOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEAVE_TYPE_LABEL = {
  annual: 'Annual',
  sick: 'Sick',
  unpaid: 'Unpaid',
  long_service: 'Long-Service',
};

function fmtDateShort(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso || '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
  });
}

export default function OnLeaveBanner({ variant = 'inline' }) {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/leave/upcoming`, {
          credentials: 'include',
        });
        if (r.ok) {
          const j = await r.json();
          if (!cancelled) setLeaves(j.leaves || []);
        }
      } catch (_e) {
        /* noop — empty state is fine */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000); // 5 min
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (loading) return null;
  if (!leaves.length) {
    // Card variant shows a friendly empty state; inline hides entirely.
    if (variant === 'card') {
      return (
        <Card
          className="p-4 border-slate-200 bg-slate-50/40"
          data-testid="on-leave-banner-card-empty"
        >
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <CalendarOff className="h-4 w-4" />
            <span>No upcoming leaves — full team available.</span>
          </div>
        </Card>
      );
    }
    return null;
  }

  // Card variant — used on Overview tab as 3rd widget.
  if (variant === 'card') {
    return (
      <Card
        className="p-5 border-amber-200 bg-amber-50/30"
        data-testid="on-leave-banner-card"
      >
        <h4 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Plane className="h-5 w-5 text-amber-600" />
          On Leave / Upcoming
          <span className="text-xs font-normal text-slate-500 ml-1">
            ({leaves.length})
          </span>
        </h4>
        <ul className="space-y-1.5" data-testid="on-leave-card-list">
          {leaves.map((l) => (
            <li
              key={l.request_id}
              className={`flex items-center gap-3 px-3 py-2 rounded-md border ${
                l.is_active
                  ? 'bg-amber-100/60 border-amber-300'
                  : 'bg-white border-slate-200'
              }`}
              data-testid={`on-leave-card-row-${l.request_id}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  l.is_active
                    ? 'bg-amber-600 text-white'
                    : 'bg-slate-200 text-slate-700'
                }`}
              >
                {(l.employee_name || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">
                  {l.employee_name}
                </p>
                <p className="text-[11px] text-slate-600">
                  {LEAVE_TYPE_LABEL[l.leave_type] || l.leave_type} ·{' '}
                  {fmtDateShort(l.start_date)} → {fmtDateShort(l.end_date)}
                </p>
              </div>
              <span
                className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                  l.is_active
                    ? 'bg-amber-600 text-white'
                    : 'bg-slate-200 text-slate-700'
                }`}
              >
                {l.is_active ? 'On leave' : 'Upcoming'}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    );
  }

  // Inline variant — fits next to the calendar on /job-cards.
  return (
    <div
      className="flex items-start gap-2 mt-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50/40"
      data-testid="on-leave-banner-inline"
    >
      <Plane className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 mb-1">
          On Leave / Upcoming
        </p>
        <div className="flex flex-wrap gap-1.5" data-testid="on-leave-pills">
          {leaves.map((l) => (
            <span
              key={l.request_id}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${
                l.is_active
                  ? 'bg-amber-600 text-white border-amber-700'
                  : 'bg-white text-slate-700 border-slate-300'
              }`}
              title={`${LEAVE_TYPE_LABEL[l.leave_type] || l.leave_type} leave · ${l.start_date} → ${l.end_date}`}
              data-testid={`on-leave-pill-${l.request_id}`}
            >
              <strong className="font-semibold">{l.employee_name}</strong>
              <span className="opacity-75">·</span>
              <span className="tabular-nums">
                {fmtDateShort(l.start_date)} → {fmtDateShort(l.end_date)}
              </span>
              {l.is_active && (
                <span className="ml-0.5 px-1 py-0 rounded bg-white/20 text-[9px] uppercase tracking-wide">
                  active
                </span>
              )}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
